<template>
    <div class="highest-index">
        <Dialog v-model:visible="visible" :style="{ width: '85vw' }" header="Add New Group" modal @hide="emit('hide-dialog', true)">
            <div class="card p-fluid">
                <h5>Enter Group Information</h5>
                <div class="formgrid grid">
                    <div class="field col-3">
                        <label for="code">Code (must be unique)</label>
                        <InputText id="code" type="text" v-model="group.code" autofocus :class="{ 'p-invalid': submitted && formErrors.code }" placeholder="e.g  read_abc" />
                        <small class="p-invalid" v-if="submitted && formErrors.code">{{ formErrors.code }}</small>
                    </div>
                    <div class="field col-3">
                        <label for="description">Description</label>
                        <InputText id="description" type="text" v-model="group.description" autofocus :class="{ 'p-invalid': submitted && formErrors.description }" placeholder="e.g  read/write full ... of abc ...." />
                        <small class="p-invalid" v-if="submitted && formErrors.description">{{ formErrors.description }}</small>
                    </div>
                    <div class="field col-1">
                        <div class="mb-2">All Users</div>
                        <ToggleButton v-model="group.allUsers" onLabel="True" offLabel="False" :style="{ width: '7em' }" />
                    </div>
                    <div class="field col-1">
                        <div class="mb-2">Active</div>
                        <ToggleButton v-model="group.active" onLabel="True" offLabel="False" :style="{ width: '7em' }" />
                    </div>
                    <div class="field col-12 lg:col-8">
                        <label for="permissions">Add Permissions</label>
                        <br />
                        <small class="p-invalid" v-if="submitted && formErrors.permissions">{{ formErrors.permissions }}</small>
                        <PickList v-model="permissionSources" dataKey="id" autofocus :class="{ 'p-invalid': submitted && formErrors.permissions }">
                            <template #sourceheader> All Permission List </template>
                            <template #targetheader> Added Permission List </template>
                            <template #item="slotProps">
                                <div>{{ slotProps.item.code }}</div>
                            </template>
                        </PickList>
                    </div>
                    <div class="field col-2">
                        <Button label="Submit" class="my-4" @click="submit"></Button>
                    </div>
                </div>
            </div>
        </Dialog>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { ref, onMounted, defineEmits, defineProps, watch } from 'vue';
import { object, string, array } from 'yup';
import { groupStore, permissionStore } from '../../../store/global-stores';
import { handleValidationErrors } from '@/utilities/form-errors-handler';

const emit = defineEmits(['hide-dialog']);
const props = defineProps({
    mode: {
        type: String,
        default: 'add',
    },
});

const { group, editGroupId, CompanyId, editPermissions } = storeToRefs(groupStore);
const { allPermissions } = storeToRefs(permissionStore);
const permissionSources = ref(null);
const visible = ref(true);
const submitted = ref(false);
const formErrors = ref([]);

const validationSchema = object({
    code: string().required('Group Code is required').min(5, 'Group code should have atleast 5 characters'),
    description: string().required('Group Description is required'),
    permissions: array().min(1, 'Atleast one permission is required to be selected'),
});

onMounted(async () => {
    // load all permissions
    const paginate = false;
    await groupStore.fetchUsersForDropdown(editGroupId.value);
    await permissionStore.fetchAllPermissions({}, paginate);
    // after permissions are fetched it can be accessed with the following getters
    permissionSources.value = [allPermissions.value, []];
    //if its in edit mode then fetch the existing permissions from editPermissions which is created when editing group from a table
    if (props.mode === 'edit') {
        let permissionIds = editPermissions.value?.map((permission) => permission.id);
        permissionSources.value[1] = editPermissions.value;
        permissionSources.value[0] = permissionSources.value[0]?.filter((item) => !permissionIds.includes(item.id));
    } else {
        // if its a new group then, create from fresh
        groupStore.initGroup();
    }
});

// watch for permissionSources array changes, and everytime it changes, we will update the permissions array in the group state
watch(permissionSources, () => {
    if (permissionSources.value) {
        group.value.permissions = permissionSources.value[1]?.map((permission) => permission.id);
    }
});

const submit = async () => {
    try {
        submitted.value = true;
        // validate the schema first
        await validationSchema.validate(group.value, { abortEarly: false });
        // if valid then submit the group form
        group.value.company_id = CompanyId;
        // if its add mode then create a new group, else update the existing group details
        const response = props.mode === 'add' ? await groupStore.createGroup(group.value) : await groupStore.updateGroup(editGroupId.value, group.value);
        if (response) {
            emit('hide-dialog', true);
            await groupStore.getAllGroups()
        }
    } catch (error) {
        if (error.inner && typeof error.inner === 'object') {
            formErrors.value = handleValidationErrors(error);
        }
    }
};
</script>
